import React, {
  ReactElement,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMount } from 'react-use'

import { ToastTriggerContext } from 'src/context/toast.context'
import {
  CSV_ONLY_DOWNLOAD_ORG_CODES,
  ENABLE_UPDATING_REGISTERED_STATUS,
  isParcoCsvOrg,
} from 'src/domain/org'
import {
  isCsvExportable,
  isReportStatusBeforeReported,
} from 'src/domain/reportStatus'
import { useApiSalesDate } from 'src/hooks/useApiSalesDate'
import { useApiSalesReports } from 'src/hooks/useApiSalesReportsByStore'
import { useApiStoreSCID } from 'src/hooks/useApiStoreSCID'
import { useApiUpdateTotals } from 'src/hooks/useApiUpdateTotals'
import useAppTitle from 'src/hooks/useAppTitle'
import useBeforeUnload from 'src/hooks/useBeforeUnload'
import Path, { StorePathParams } from 'src/routes/path'
import {
  deselectedTenants,
  setConfirmationDate,
  setSelectedTenantCodes,
  DEFAULT_DATE,
} from 'src/slices/confirmSalesReport/confirmSalesReportSlice'
import { clearPutDefiniteValues } from 'src/slices/definiteValues/definiteValuesSlice'
import { clearMessage } from 'src/slices/message/messageSlice'
import {
  ProgressStatusEnum,
  RoleEnum,
  SalesReportWithTenant,
  TenantSalesReportSummary,
} from 'src/slices/services/api'
import { success } from 'src/slices/snackbar/snackbarSlice'
import {
  getStore,
  selectStoreByCode,
  selectStoresRequestStateByParams,
} from 'src/slices/stores/storesSlice'
import {
  clearTenantSalesReportSummaries,
  getTenantSalesReportSummaries,
  postBulkFinalizationOfSalesDates,
  postSalesReportsCSV,
  resetBulkFinalizationStatus,
  resetRevertReportedStatus,
  resetSyncStatus,
  revertReportedStatus,
  selectDenormalizedTenantSalesReportSummariesByParams,
  selectStoreSalesReportSummariesRequestStateByParams,
} from 'src/slices/tenantSalesReportSummaries/tenantSalesReportSummariesSlice'
import { useAppDispatch, useAppSelector } from 'src/store'

import { downloadCSV } from '../downloadCSV'
import { downloadParcoCSV } from '../downloadParcoCSV'
import { checkDownloadPrerequisites } from '../downloadPrerequisites'
import { downloadCsvExcelZip } from '../downloadZip'
import TemplatesTenantSalesReportSummary from '../templates'
import { DownloadResult } from '../type'
import { processUnifiedSalesData } from '../verificationSheetAggregator'
import { generateVerificationSheetExcel } from '../verificationSheetExcelGenerator'
import Presenter from './presenter'

const PagesTenantSalesReportSummary: React.FC = (): ReactElement => {
  useAppTitle('テナント売上報告一覧')
  const navigate = useNavigate()
  const toastContext = useContext(ToastTriggerContext)
  const { getAllByStore } = useApiSalesReports()
  const { getStoreSCID } = useApiStoreSCID()
  const { patchUpdateTotals } = useApiUpdateTotals()
  const { updateStatus } = useApiSalesDate()

  const [isReadLoaded, setIsReadLoaded] = useState<boolean | undefined>()
  const [isCSVExcelDownloading, setIsCSVExcelDownloading] =
    useState<boolean>(false)

  // Query
  const { date } = useAppSelector((state) => state.forms.confirmSalesReport)
  const role: RoleEnum | undefined = useAppSelector(
    (state) => state.auth.currentAuthorization?.role
  )
  const { orgCode, storeCode } = useParams<StorePathParams>() as StorePathParams

  // state
  const requestParam = useMemo<{
    orgCode: string
    storeCode: string
    date: string
  }>(() => {
    return {
      orgCode,
      storeCode,
      date: date || DEFAULT_DATE,
    }
  }, [date, orgCode, storeCode])

  // Redux
  const dispatch = useAppDispatch()
  useMount(() => {
    dispatch(deselectedTenants())
    dispatch(clearPutDefiniteValues())
    dispatch(clearMessage())
  })

  // get store
  const store = useAppSelector(selectStoreByCode({ orgCode, storeCode }))
  const storeRequestState = useAppSelector(
    selectStoresRequestStateByParams({ orgCode, storeCode })
  )
  useEffect(() => {
    if (store === undefined && storeRequestState.status === 'idle') {
      dispatch(getStore({ orgCode, storeCode }))
    }
  }, [dispatch, store, orgCode, storeCode, storeRequestState.status])

  // get summary
  const summaries: TenantSalesReportSummary[] | undefined = useAppSelector(
    selectDenormalizedTenantSalesReportSummariesByParams(requestParam)
  )
  const summaryRequestState = useAppSelector(
    selectStoreSalesReportSummariesRequestStateByParams(requestParam)
  )

  const revertReportedRequestState = useAppSelector(
    (state) => state.entities.tenantSalesReportSummaries.revertReportedStatus
  )
  const bulkFinalizationRequestState = useAppSelector(
    (state) => state.entities.tenantSalesReportSummaries.bulkFinalizationStatus
  )
  const syncRequestState = useAppSelector(
    (state) => state.entities.tenantSalesReportSummaries.syncStatus
  )

  const isUpdateLoading =
    revertReportedRequestState === 'loading' ||
    bulkFinalizationRequestState === 'loading' ||
    syncRequestState === 'loading'

  useBeforeUnload(isUpdateLoading)

  useEffect(() => {
    switch (summaryRequestState.status) {
      case 'idle':
        dispatch(getTenantSalesReportSummaries(requestParam))
        break
      case 'loading':
        setIsReadLoaded(false)
        break
      case 'failed':
      case 'succeeded':
        setIsReadLoaded(true)
        break
      default:
        setIsReadLoaded(false)
    }
  }, [dispatch, requestParam, summaryRequestState.status])

  const selectedTenantIndex = useAppSelector(
    (state) => state.forms.confirmSalesReport.selectedTenantIndex
  )

  // 報告済み/再報告済みに一括で戻す
  const handleClickRevertReported = async (selectedSalesDateIds: string[]) => {
    if (selectedSalesDateIds.length === 0) return

    // NOTE: progress_statusからregisteredを撤廃後に修正。
    // appのデータ連携完了時にOIOIとAEONはregisteredに、他のORGはcompletedになるため
    const progressStatus: ProgressStatusEnum =
      ENABLE_UPDATING_REGISTERED_STATUS.includes(orgCode)
        ? ProgressStatusEnum.Registered
        : ProgressStatusEnum.Completed

    await dispatch(
      revertReportedStatus({
        orgCode,
        storeCode,
        salesDateIds: selectedSalesDateIds,
        progressStatus,
      })
    )
  }

  // 一括確定値登録
  const handleClickBulkFinalization = (selectedSalesDateIds: string[]) => {
    if (selectedSalesDateIds.length === 0) return
    dispatch(
      postBulkFinalizationOfSalesDates({
        orgCode,
        storeCode,
        salesDateIds: selectedSalesDateIds,
      })
    )
  }

  const handleReloadPage = () => {
    dispatch(getTenantSalesReportSummaries({ ...requestParam }))
  }

  // パルコ(013)用CSV単体ダウンロード処理。確定前のOCR値を1テナント1行で出力する (Issue #3408)
  const downloadParcoCsvOnly = (
    targetSalesReports: SalesReportWithTenant[]
  ): DownloadResult => {
    if (!targetSalesReports || targetSalesReports.length === 0 || !store) {
      return { success: false, message: '対象データがありません' }
    }

    downloadParcoCSV(
      storeCode,
      store.name,
      requestParam.date,
      targetSalesReports
    )

    return { success: true, message: 'CSVダウンロードに成功しました' }
  }

  // CSV単体ダウンロード処理（009用）
  const downloadCsvOnly = async (
    targetSalesReports: SalesReportWithTenant[]
  ): Promise<DownloadResult> => {
    if (!targetSalesReports || targetSalesReports.length === 0) {
      return { success: false, message: '対象データがありません' }
    }

    const scidResult = await getStoreSCID(orgCode, storeCode)
    if (!scidResult.success || !scidResult.data?.scid) {
      return {
        success: false,
        message: `SCIDの取得に失敗しました: ${
          scidResult.message || 'SCIDが設定されていません'
        }`,
      }
    }

    downloadCSV(
      orgCode,
      storeCode,
      requestParam.date,
      targetSalesReports,
      scidResult.data.scid
    )

    return { success: true, message: 'CSVダウンロードに成功しました' }
  }

  // ZIP（CSV + Excel）ダウンロード処理
  const downloadZip = async (
    targetSalesReports: SalesReportWithTenant[]
  ): Promise<DownloadResult> => {
    if (!targetSalesReports || targetSalesReports.length === 0 || !store) {
      return { success: false, message: '対象データがありません' }
    }

    const reportedTenantCount =
      summaries?.filter(
        (summary) => !isReportStatusBeforeReported(summary.reportStatus)
      ).length ?? 0
    const requiredToReportCount = summaries?.length ?? 0

    const processedData = processUnifiedSalesData(
      targetSalesReports,
      orgCode,
      storeCode,
      store.name,
      reportedTenantCount,
      requiredToReportCount
    )

    const excelResult = generateVerificationSheetExcel(processedData)
    if (!excelResult.success) {
      return {
        success: false,
        message: 'ZIP（CSV・Excel）ファイルの生成に失敗しました',
      }
    }

    return downloadCsvExcelZip({
      workbook: excelResult.workbook,
      orgCode,
      storeCode,
      storeName: store.name,
      date: requestParam.date,
      salesReports: targetSalesReports,
    })
  }

  // ステータス更新処理
  const updateSyncStatus = async (
    targetSalesReports: { id: string }[]
  ): Promise<{ success: boolean; message?: string }> => {
    const result = await updateStatus(
      orgCode,
      storeCode,
      targetSalesReports.map((salesReport) => salesReport.id),
      ProgressStatusEnum.FinalizedReportSynced
    )
    return result
  }

  const handleDownloadCSV = async () => {
    setIsCSVExcelDownloading(true)

    try {
      const [salesReports, updateTotalsResult] = await Promise.all([
        getAllByStore(orgCode, storeCode, requestParam.date, requestParam.date),
        patchUpdateTotals(
          orgCode,
          requestParam.date,
          requestParam.date,
          storeCode
        ),
      ])

      const prerequisites = checkDownloadPrerequisites(
        salesReports,
        updateTotalsResult,
        store !== undefined
      )
      if (!prerequisites.success) {
        toastContext.sendToast({
          variant: 'error',
          title: prerequisites.message,
        })
        return
      }

      // 出力対象のステータスはorgごとに異なる（既定=確定済みのみ / パルコ=報告済み・再送済みのみ）
      const targetSalesReports = prerequisites.salesReports.filter(
        (salesReport) => isCsvExportable(orgCode, salesReport.reportStatus)
      )

      // ダウンロード処理（パルコ: 専用フォーマットのCSV単体、009: CSV単体、それ以外: ZIP）
      let downloadResult: DownloadResult
      if (isParcoCsvOrg(orgCode)) {
        downloadResult = downloadParcoCsvOnly(targetSalesReports)
      } else if (CSV_ONLY_DOWNLOAD_ORG_CODES.includes(orgCode)) {
        downloadResult = await downloadCsvOnly(targetSalesReports)
      } else {
        downloadResult = await downloadZip(targetSalesReports)
      }

      if (!downloadResult.success) {
        toastContext.sendToast({
          variant: 'error',
          title: downloadResult.message,
        })
        return
      }

      // ステータス更新
      const statusResult = await updateSyncStatus(targetSalesReports)
      if (!statusResult.success) {
        toastContext.sendToast({
          variant: 'error',
          title: `ステータス更新に失敗しました: ${statusResult.message}`,
        })
        return
      }

      toastContext.sendToast({
        variant: 'success',
        title: downloadResult.message,
      })
      dispatch(getTenantSalesReportSummaries({ ...requestParam }))
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '不明なエラー'
      toastContext.sendToast({
        variant: 'error',
        title: `ファイルの処理中にエラーが発生しました: ${errorMessage}`,
      })
    } finally {
      setIsCSVExcelDownloading(false)
    }
  }

  // 確定値の確認
  const handleClickStartFinalization = (selectedTenantCodes: string[]) => {
    if (selectedTenantCodes.length === 0) return
    dispatch(setSelectedTenantCodes(selectedTenantCodes))
    dispatch(setConfirmationDate(requestParam.date))
    navigate(
      Path.tenantSalesReport(
        orgCode,
        storeCode,
        selectedTenantCodes[selectedTenantIndex]
      )
    )
  }

  // データ連携
  const handleClickSend = (selectedSalesDateIds: string[]) => {
    if (selectedSalesDateIds.length === 0) return
    dispatch(
      postSalesReportsCSV({
        orgCode,
        storeCode,
        salesDateIds: selectedSalesDateIds,
      })
    )
  }

  const handleChangeDate = (newDate: string) => {
    dispatch(setConfirmationDate(newDate)) // 日付変更をトリガーに、データを再取得する。
    setIsReadLoaded(false)
  }

  useEffect(() => {
    if (
      revertReportedRequestState === 'idle' ||
      revertReportedRequestState === 'loading'
    )
      return
    if (revertReportedRequestState === 'succeeded') {
      toastContext.sendToast({
        variant: 'success',
        title: '報告済み/再送信済みに一括変更しました。',
      })
      dispatch(clearTenantSalesReportSummaries())
      dispatch(resetRevertReportedStatus())
      return
    }
    toastContext.sendToast({
      variant: 'error',
      title: '報告済み/再送信済みへの一括変更に失敗しました',
    })
    dispatch(resetRevertReportedStatus())
  }, [dispatch, revertReportedRequestState, toastContext])

  useEffect(() => {
    if (
      bulkFinalizationRequestState === 'idle' ||
      bulkFinalizationRequestState === 'loading'
    )
      return
    if (bulkFinalizationRequestState === 'succeeded') {
      toastContext.sendToast({
        variant: 'success',
        title: '確定値を一括登録しました。',
      })
      dispatch(clearTenantSalesReportSummaries())
      dispatch(resetBulkFinalizationStatus())
      return
    }
    toastContext.sendToast({
      variant: 'error',
      title: '確定値の一括登録に失敗しました',
    })
    dispatch(resetBulkFinalizationStatus())
  }, [dispatch, bulkFinalizationRequestState, toastContext])

  useEffect(() => {
    if (syncRequestState === 'succeeded') {
      dispatch(success('データ連携が完了しました。'))
      // NOTE: データ連携完了後、ステータスを最新化するために、一度クリアする
      dispatch(clearTenantSalesReportSummaries())
      dispatch(resetSyncStatus())
    }
  }, [dispatch, syncRequestState])

  return (
    <TemplatesTenantSalesReportSummary
      isReadLoaded={isReadLoaded}
      isUpdateLoading={isUpdateLoading}
      isCSVDownloading={isCSVExcelDownloading}
      role={role}
      date={requestParam.date}
      breadcrumbs={Presenter.breadcrumbs(orgCode, store?.name)}
      storeName={store?.name || ''}
      storeCode={store?.code || ''}
      orgCode={orgCode}
      summaries={summaries || []}
      onDownloadCSV={handleDownloadCSV}
      onChangeDate={handleChangeDate}
      onClickRevertReported={handleClickRevertReported}
      onClickBulkFinalization={handleClickBulkFinalization}
      onClickStartFinalization={handleClickStartFinalization}
      onClickSend={handleClickSend}
      onReloadPage={handleReloadPage}
    />
  )
}

export default PagesTenantSalesReportSummary
